import { JSX, useCallback, useMemo } from "react";

import { read, utils } from "xlsx";

import FormControl from "@mui/material/FormControl";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";

import { asTitle } from "utils/Utils";

export type FileUploadProps = {
	label?: string;

	onLoadData: (data: string[][]) => void;
};

export default function FileUpload(
	props: Readonly<FileUploadProps>
): JSX.Element {
	const processFile = useCallback((evt: ProgressEvent<FileReader>): void => {
		if (!evt.target?.result) {
			return;
		}

		const bstr = evt.target.result;
		const wb = read(bstr, { type: "array" });
		const ws = wb.Sheets[wb.SheetNames[0]];
		const data = utils.sheet_to_json<string[]>(ws, { header: 1 });
		props.onLoadData(data);
	}, []);

	const reader: FileReader = useMemo(() => {
		const fileReader = new FileReader();
		fileReader.onload = processFile;
		return fileReader;
	}, [processFile]);

	const readFile = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			event.preventDefault();

			if (!event.target.files || event.target.files.length === 0) {
				return;
			}

			const f = event.target.files[0];
			reader.readAsArrayBuffer(f);
		},
		[reader]
	);

	return (
		<FormControl fullWidth variant="standard">
			{props.label && (
				<InputLabel shrink={true}>{asTitle(props.label)}</InputLabel>
			)}
			<Input
				type="file"
				inputProps={{ accept: ".xlsx,.xls,.csv" }}
				onChange={readFile}
			/>
		</FormControl>
	);
}
